import { emailRegExp } from "../helpers/index";
import * as Yup from "yup";
const onlyAlaphaNumericRegExp = /^[a-zA-Z0-9\s]+$/;

//signin
export const SignInValidationSchema = Yup.object().shape({
    email: Yup.string()
        .strict()
        .trim("The email cannot include leading and trailing spaces")
        .required("Email is required")
        .matches(emailRegExp, "Email is invalid"),
    password: Yup.string()
        .strict()
        .trim("The password cannot include leading and trailing spaces")
        .required("Password is required"),
});

//forgot password
export const ForgotPswdValidationSchema = Yup.object().shape({
    email: Yup.string()
        .strict()
        .trim("The email cannot include leading and trailing spaces")
        .required("Email is required")
        .matches(emailRegExp, "Email is invalid"),
});

//reset password
export const resetPswdValidationSchema = Yup.object().shape({
    password: Yup.string()
        .strict()
        .trim("The password cannot include leading and trailing spaces")
        .required("Password is required")
        .min(6, "Password min length text")
        .max(12, "Max length text")
        .test(
            "uppercase",
            "Must include at least one uppercase letter",
            (value) => /[A-Z]/.test(value || "")
        )
        .test(
            "lowercase",
            "Must include at least one lowercase letter",
            (value) => /[a-z]/.test(value || "")
        )
        .test("number", "Must include at least one number", (value) =>
            /\d/.test(value || "")
        )
        .test(
            "specialChar",
            "Must include at least one special character (!@#$%^&*)",
            (value) => /[!@#$%^&*]/.test(value || "")
        ),
    confirmPassword: Yup.string()
        .strict()
        .trim("The confirm password cannot include leading and trailing spaces")
        .required("Confirm password is required")
        .oneOf([Yup.ref("password"), null], "Unmatched Password"),
});

export const changePswdValidationSchema = Yup.object().shape({
    currentPassword: Yup.string()
        .strict()
        .trim("The current password cannot include leading and trailing spaces")
        .when("password_empty", {
            is: false,
            then: (schema) =>
                schema
                    .required("Current password is required")
                    .min(6, "Current password must be at least 6 characters")
                    .max(12, "Maximum length is 12 characters"),
            otherwise: (schema) => schema.notRequired(),
        }),
    // .required("Current password is required")
    // .min(6, "Current password must be at least 6 characters")
    // .max(12, "Maximum length is 12 characters"),
    newPassword: Yup.string()
        .strict()
        .trim("The password cannot include leading and trailing spaces")
        .required("Password is required")
        .min(6, "Password must be at least 6 characters")
        .max(12, "Maximum length is 12 characters")
        .test("uppercase", "Upper Case text", (value) =>
            /[A-Z]/.test(value || "")
        )
        .test("lowercase", "Lower Case text", (value) =>
            /[a-z]/.test(value || "")
        )
        .test("number", "Number Case text", (value) => /\d/.test(value || ""))
        .test("specialChar", "Special Case text", (value) =>
            /[!@#$%^&*]/.test(value || "")
        ),
    confirmPassword: Yup.string()
        .strict()
        .trim("The confirm password cannot include leading and trailing spaces")
        .required("Confirm password is required")
        .oneOf([Yup.ref("newPassword"), null], "Unmatched Password"),
});

export const passwordSchema = Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .test("uppercase", "Upper Case text", (value) => /[A-Z]/.test(value || ""))
    .test("lowercase", "Lower Case text", (value) => /[a-z]/.test(value || ""))
    .test("number", "Number Case text", (value) => /\d/.test(value || ""))
    .test("specialChar", "Special Case text", (value) =>
        /[!@#$%^&*]/.test(value || "")
    );

//seller page
export const SellerPageValidationSchema = Yup.object().shape({
    email: Yup.string()
        .strict()
        .trim("The email cannot include leading and trailing spaces")
        .required("Email is required")
        .matches(emailRegExp, "Email is invalid"),

    name: Yup.string()
        .strict()
        .trim("The full name cannot include leading and trailing spaces")
        .required("Full name is required.")
        .min(2, "Full name must be at least 2 characters")
        .max(50, "Full name must not exceed 50 characters.")
        .matches(
            onlyAlaphaNumericRegExp,
            "No special character allowed for this field"
        ),

    phone_number: Yup.string()
        .required("Phone number is required")
        .test(
            "is-valid-phone",
            "Please enter a valid phone number",
            function (value) {
                if (!value) return false;

                const input = document.querySelector(
                    `input[name="${this.path}"]`
                );

                if (!input || !window.$) return false;

                const iti = window.$(input).intlTelInput();
                return iti.intlTelInput("isValidNumber");
            }
        ),

    property_location: Yup.string()
        .strict()
        .trim(
            "The property location cannot include leading and trailing spaces"
        )
        .notRequired()
        .test("property-location-validation", function (value) {
            if (!value || value.trim() === "") return true;

            if (value.length < 5) {
                return this.createError({
                    message: "Property location must be at least 5 characters",
                });
            }

            if (value.length > 150) {
                return this.createError({
                    message:
                        "Property location must not exceed 150 characters.",
                });
            }

            // if (!onlyAlaphaNumericRegExp.test(value)) {
            //     return this.createError({
            //         message: "No special character allowed for this field",
            //     });
            // }

            return true;
        }),
    property_type: Yup.mixed()
        .transform((value) =>
            value === "" || value === null || value === undefined
                ? undefined
                : String(value)
        )
        .required("Property type is required"),
});

export const SignUpValidationSchema = Yup.object().shape({
    account_type: Yup.string().required("Account type is required"),

    licence_number: Yup.string().when("account_type", {
        is: (val) => val === "2",
        then: (schema) =>
            schema
                .required("License number is required")
                .trim(
                    "The license number cannot include leading and trailing spaces"
                )
                .matches(/^[0-9]+$/, "License number must contain only numbers")
                .min(5, "The license number must be at least 5 digits")
                .max(15, "The license number must not exceed 15 digits"),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),

    emirates_id: Yup.string().when("account_type", {
        is: (val) => val === "2",
        then: (schema) =>
            schema
                .required("Emirates ID is required")

                .test(
                    "digits-only",
                    "Emirates ID must contain only numbers",
                    (value) => {
                        if (!value) return true;
                        return /^[0-9- ]+$/.test(value);
                    }
                )

                .test(
                    "length",
                    "Emirates ID must be exactly 15 digits",
                    (value) => {
                        if (!value) return true;
                        const digits = value.replace(/\D/g, "");
                        return digits.length === 15;
                    }
                )

                .test(
                    "starts-with-784",
                    "Emirates ID must start with 784",
                    (value) => {
                        if (!value) return true;
                        return value.replace(/\D/g, "").startsWith("784");
                    }
                ),

        otherwise: (schema) => schema.notRequired().nullable(),
    }),

    licence_issuing_emirate: Yup.string().when("account_type", {
        is: (val) => val === "2",
        then: (schema) =>
            schema.required("License issuing emirate is required."),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
});

export const PurchaseModalValidationSchema = Yup.object().shape({
    signature: Yup.string()
        .strict(true)
        .trim("The full name cannot include leading or trailing spaces")
        .required("Signature is required")
        .min(2, "Full name must be at least 2 characters")
        .max(50, "Full name must not exceed 50 characters")
        .matches(onlyAlaphaNumericRegExp, "No special characters are allowed"),

    agreement: Yup.boolean().oneOf(
        [true],
        "You must agree to the terms and conditions before proceeding"
    ),
});

export const ForefitModalValidationSchema = Yup.object().shape({
    signature: Yup.string()
        .strict(true)
        .trim("The full name cannot include leading or trailing spaces")
        .required("Signature is required")
        .min(2, "Full name must be at least 2 characters")
        .max(50, "Full name must not exceed 50 characters")
        .matches(onlyAlaphaNumericRegExp, "No special characters are allowed"),

    agreement: Yup.boolean().oneOf(
        [true],
        "You must agree to the terms and conditions before proceeding"
    ),
});

export const DeleteAccountSchema = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .email("Enter a valid email address"),

    reason: Yup.string().required("Reason is required"),

    otherReason: Yup.string().when("reason", {
        is: "other",
        then: (schema) =>
            schema
                .trim("The other Reason cannot include leading or trailing spaces")
                .required("Please specify your reason")
                .min(10, "Reason must be at least 10 characters")
                .max(250, "Reason cannot exceed 250 characters"),
        otherwise: (schema) => schema.notRequired(),
    }),
});
